import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { formatStringLowercaseWithDashes } from 'shared/utils/formatStringLowercaseWithDashes';
import { IProductLandingPage } from 'shared/types/ProductLandingPage';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { startCase } from 'shared/utils/startCase';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const CategorizedBrandPage = ({
  brandName,
  brandDisplayName,
  breadcrumbs,
  browseKey,
  browseValue,
  category,
  constructorResponse,
  displayName,
  globalPricingId,
  subCategory,
  isCrawler = false,
}: IProductLandingPage) => {
  const {
    name = displayName,
    description = '',
    metadataDescription = null,
    titleTag = '',
  } = breadcrumbs || {};

  return (
    <PLPLandingPageComponent
      breadcrumbs={[
        { name: startCase(category), slug: `/${category}` },
        {
          name: brandDisplayName,
          slug: `/brand/${formatStringLowercaseWithDashes(brandName)}`,
        },
        {
          name: startCase(subCategory),
          slug: `/${category}/${formatStringLowercaseWithDashes(subCategory)}`,
        },
      ]}
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag}
      isCrawler={isCrawler}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const {
    pageSlug: category,
    brandName,
    subCategory,
  }: { pageSlug: string; brandName: string; subCategory: string } = query;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${category}/brand/${brandName}/${subCategory}`,
  });
  const isCrawler = isSearchCrawler(req?.headers);

  try {
    let presentationBrandName: string = startCase(brandName);
    const presentationSubcategory: string = startCase(subCategory);
    const brandBrowseName: string = startCase(brandName, {
      ignoreSpecialDisplay: true,
    }).toLowerCase();
    const normalizedBrandName = normalizeValue(brandBrowseName);

    // BUY3-1381: need to redirect special character brand names to normalized value
    // as Google is still crawling V1 GOAT
    if (normalizedBrandName !== brandBrowseName) {
      return {
        redirect: {
          destination: getRedirectionPath(
            `/${category}/brand/${formatStringLowercaseWithDashes(
              normalizedBrandName,
            )}/${subCategory}`,
            locale,
          ),
          permanent: true,
        },
      };
    }

    const brandResponse = await getBrowseResults({
      req,
      res,
      resolvedUrl,
      query,
      filterKey: 'brand',
      filterValue: brandBrowseName,
    });

    const invalidSearch = brandResponse.response.total_num_results === 0;

    if (invalidSearch) {
      return {
        notFound: true,
      };
    }

    const brandFacet = brandResponse.response.facets?.find((facet) => facet?.name === 'brand');
    if (brandFacet) {
      presentationBrandName = brandFacet?.options[0]?.display_name;
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        brandName,
        brandDisplayName: presentationBrandName,
        breadcrumbs,
        browseKey: 'brand',
        browseValue: brandBrowseName,
        category,
        constructorResponse: brandResponse,
        displayName: `${presentationBrandName} ${presentationSubcategory}`,
        globalPricingId,
        subCategory,
        isCrawler,
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default CategorizedBrandPage;
